import { BackofficeUser } from "../../../../types/BackofficeUser";
import * as stagesCase from "../../../Stages/StagesCase";
import { STAGE_ACTION_REQUIRED } from "../../../Stages/StagesCase";
import personFields from "../modules/personFields";
import {
  addProcessParticipant,
  caseInsurance,
  extraJudicialProcedure,
  getAdditionalClientsForm,
  getNextInstanceSection,
  getUnderageClientForm,
  litigationCourt,
  opponentLawyer,
  processParticipantsOrganizations,
  processParticipantsPeople,
  prosecution,
} from "../abstractLitigationCase/fullFormDefinition";
import {
  dateFieldDefault,
  emailDefault,
  faxNumberDefault,
  floatFieldNonNegativeDefault,
  ibanFieldDefault,
  postalCodeDefault,
  textFieldDefault,
} from "../../../validationRules";
import addressFields from "../modules/addressFields";
import { trueFalseDefaultOptions } from "../formDefinitionFunctions";
import InputAdornment from "@mui/material/InputAdornment";
import { IbanInput } from "../../../../components/MaskedInputs/IbanInput";
import React from "react";
import _ from "lodash";
import { UseForm } from "../../../../types/UseForm";
import legalForms from "../../../legalForms";
import { arrayUtils } from "../../../utils";
import { overwriteJudicialProcedure } from "./overwriteJudicialProcedure";
import { translate } from "../../../Translations/translatorService";
import { ProductUnion } from "../../../../types/ProductUnion";
import { EmploymentContract } from "../../../../types/Products/EmploymentLaw/EmploymentContract";
import { getCourtAppointmentFields } from "../modules/courtAppointmentFields";
import { debtCollectionAgencyFields } from "../modules/debtCollectionFields";

export const fullFormDefinition = (product: EmploymentContract, currentUser: BackofficeUser) => {
  const contractBaseCreated = {
    isHidden: ({ product }: { product: ProductUnion }) => (product.stage || 0) > stagesCase.STAGE_CREATED,
    label: "Allgemeines zum Vertrag",
    elements: [...personFields({ path: `${product.productClassName}.client`, showDiverseGenderOption: true })],
  };

  const employmentContractBase = {
    isHidden: ({ product }: { product: EmploymentContract }) => (product.stage || 0) < stagesCase.STAGE_DATA_COMPLETED,
    label: "Allgemeines zum Arbeitsvertrag",
    elements: [
      {
        type: "ValidatorSelect",
        path: `${product.productClassName}.caseCategory`,
        isDisabled: ({ product }: { product: EmploymentContract }) =>
          (product.stage || 0) > STAGE_ACTION_REQUIRED || product.caseCategory === "advisory",
        options: ({ product }: { product: EmploymentContract }) => {
          return product.caseCategory === "advisory"
            ? [{ value: "enforceClaim" }, { value: "defendClaim" }, { value: "advisory" }]
            : [{ value: "enforceClaim" }, { value: "defendClaim" }];
        },
        alert: {
          onChange:
            "Das Ändern der Position der Mandantschaft (Klägerin/Beklagte) führt dazu, dass sich viele " +
            "Felder ändern und auch Felder verschwinden. Die neuen Felder müssen dann von Ihnen neu " +
            "befüllt werden, damit der Fall weiter bearbeitet werden kann. Möchten Sie die Position " +
            "der Mandantschaft im Verfahren tatsächlich ändern?",
        },
      },
      {
        type: "ValidatorSelect",
        path: `${product.productClassName}.affectedPartyType`,
        isDisabled: () => true,
        options: () => [{ value: "customer" }, { value: "coinsured" }, { value: "unknown" }],
      },
      ...personFields({ path: `${product.productClassName}.client`, showDiverseGenderOption: true }),
      ...addressFields({
        path: `${product.productClassName}.client.residenceAddress`,
        isHidden: ({ product }: { product: EmploymentContract }) => product.advisoryHandling,
      }),
      {
        type: "ValidatorDateField",
        path: `${product.productClassName}.client.birthDate`,
        validators: dateFieldDefault,
        isDisabled: ({ product }: { product: EmploymentContract }) => product.stage >= stagesCase.STAGE_CHECKED,
      },
      {
        type: "ValidatorDateField",
        path: `${product.productClassName}.employmentStartDate`,
        validators: dateFieldDefault,
      },
      {
        type: "ValidatorDateField",
        path: `${product.productClassName}.employmentEndDate`,
        validators: dateFieldDefault,
      },
      {
        type: "ValidatorSelect",
        path: `${product.productClassName}.employmentRegularWorkplace`,
        isDisabled: ({ product }: { product: EmploymentContract }) => product.stage >= stagesCase.STAGE_CHECKED,
        options: () => [{ value: "company" }, { value: "home" }, { value: "other" }],
      },
      {
        type: "ValidatorTextField",
        path: `${product.productClassName}.employmentRegularWorkplaceOtherAddressDescription`,
        isDisabled: ({ product }: { product: EmploymentContract }) => product.stage >= stagesCase.STAGE_COMPLETED,
        isHidden: ({ product }: { product: EmploymentContract }) => product.employmentRegularWorkplace !== "other",
      },
      ...addressFields({
        path: `${product.productClassName}.employmentRegularWorkplaceOtherAddress`,
        isHidden: ({ product }: { product: EmploymentContract }) => product.employmentRegularWorkplace !== "other",
      }),
      {
        type: "ValidatorSelect",
        path: `${product.productClassName}.clientHasAlreadyClaimed`,
        isDisabled: ({ product }: { product: EmploymentContract }) => (product.stage || 0) >= stagesCase.STAGE_CHECKED,
        isHidden: ({ product }: { product: EmploymentContract }) => product.caseCategory !== "enforceClaim",
        options: () => trueFalseDefaultOptions(`${product.productClassName}.clientHasAlreadyClaimed`),
      },
      {
        type: "ValidatorDateField",
        path: `${product.productClassName}.claimedDateClient`,
        validators: dateFieldDefault,
        isHidden: ({ product }: { product: EmploymentContract }) => product.caseCategory !== "enforceClaim",
      },
      {
        type: "ValidatorDateField",
        path: `${product.productClassName}.claimedDeadlineClient`,
        validators: dateFieldDefault,
        isHidden: ({ product }: { product: EmploymentContract }) =>
          product.caseCategory !== "enforceClaim" || !product.clientHasAlreadyClaimed,
      },
      {
        type: "ValidatorNumberField",
        path: `${product.productClassName}.claimedAmountClient`,
        isHidden: ({ product }: { product: EmploymentContract }) => product.subjectOfDispute !== "money",
        numberType: "float",
        fieldType: "money",
      },
      {
        type: "ValidatorNumberField",
        path: `${product.productClassName}.overdueFees`,
        isHidden: ({ product }: { product: EmploymentContract }) =>
          product.subjectOfDispute !== "money" ||
          (product.caseCategory !== "defendClaim" && !product.clientHasAlreadyClaimed),
        fieldType: "money",
        numberType: "float",
      },
      {
        type: "ValidatorTextField",
        path: `${product.productClassName}.client.bankData.iban`,
        validators: ibanFieldDefault,
        additionalProps: {
          InputProps: {
            startAdornment: <InputAdornment position="start">DE</InputAdornment>,
            inputComponent: IbanInput,
          },
        },
      },
      {
        type: "ValidatorSelect",
        path: `${product.productClassName}.defenseProcessStatus`,
        isDisabled: ({ product }: { product: EmploymentContract }) =>
          (product.stage || 0) >= stagesCase.STAGE_DATA_COMPLETED_LAWFIRM,
        isHidden: ({ product }: { product: EmploymentContract }) => product.caseCategory !== "defendClaim",
        options: () => [
          { value: "reminder" },
          { value: "debtCollection" },
          { value: "paymentOrder" },
          { value: "enforcementOrder" },
          { value: "lawsuit" },
          { value: "none" },
        ],
      },
    ],
  };

  const employmentContractDetails = {
    isHidden: ({ product }: { product: EmploymentContract }) =>
      (product.stage || 0) < stagesCase.STAGE_DATA_COMPLETED || product.caseCategory === "advisory",
    label: "Details zum Anliegen",
    elements: [
      {
        type: "ValidatorSelect",
        path: `${product.productClassName}.reasonsForDispute`,
        additionalProps: {
          multiple: true,
          fullWidth: true,
        },
        isDisabled: ({ product }: { product: EmploymentContract }) => product.stage >= stagesCase.STAGE_CHECKED,
        options: ({ product }: { product: EmploymentContract }) => [
          { value: "salary" },
          {
            value: "overtime",
            label:
              product.subjectOfDispute !== "money"
                ? "Überstunden / Freizeitausgleich"
                : "Finanzielle Ansprüche aus Überstunden",
          },
          {
            value: "vacation",
            label: product.subjectOfDispute !== "money" ? "Gewährung von Urlaub" : "Finanzielle Ansprüche aus Urlaub",
          },
          { value: "workCertificate" },
          { value: "parentalLeave" },
          { value: "partTime" },
          { value: "bullying" },
          { value: "claimedPayment" },
          { value: "allegedDamage" },
          { value: "writtenWarning" },
          { value: "termination" },
          { value: "itemsToReturn" },
          { value: "other" },
        ],
      },
      // salary
      {
        type: "ValidatorDateField",
        path: `${product.productClassName}.salaryOutstandingStartDate`,
        validators: dateFieldDefault,
        isHidden: ({ product }: { product: EmploymentContract }) => !product.reasonsForDispute.includes("salary"),
      },
      {
        type: "ValidatorDateField",
        path: `${product.productClassName}.salaryOutstandingEndDate`,
        validators: dateFieldDefault,
        isHidden: ({ product }: { product: EmploymentContract }) => !product.reasonsForDispute.includes("salary"),
      },
      {
        type: "ValidatorNumberField",
        path: `${product.productClassName}.grossSalary`,
        isHidden: ({ product }: { product: EmploymentContract }) =>
          !arrayUtils.getArraysIntersect(product.reasonsForDispute, ["salary", "overtime"]),
        fieldType: "money",
        numberType: "float",
      },
      {
        type: "ValidatorNumberField",
        path: `${product.productClassName}.grossSalaryClaim`,
        isHidden: ({ product }: { product: EmploymentContract }) =>
          !(product.reasonsForDispute.includes("salary") && product.subjectOfDispute === "money"),
        isDisabled: () => true,
        fieldType: "money",
        numberType: "float",
        helperText:
          "Feld wird automatisch nach Mandantenangaben berechnet, sollte aber noch einmal händisch überprüft werden",
      },
      //overtime & subjectOfDispute money
      {
        type: "ValidatorNumberField",
        path: `${product.productClassName}.overtimeAccumulatedTotal`,
        isHidden: ({ product }: { product: EmploymentContract }) =>
          !product.reasonsForDispute.includes("overtime") || product.subjectOfDispute !== "money",
        numberType: "float",
        validators: floatFieldNonNegativeDefault,
      },
      {
        type: "ValidatorNumberField",
        path: `${product.productClassName}.grossSalaryClaimFromOvertime`,
        isHidden: ({ product }: { product: EmploymentContract }) =>
          !(product.reasonsForDispute.includes("overtime") && product.subjectOfDispute === "money"),
        isDisabled: () => true,
        fieldType: "money",
        numberType: "float",
        helperText:
          "Feld wird automatisch nach Mandantenangaben berechnet, sollte aber noch einmal händisch überprüft werden",
      },
      {
        type: "ValidatorNumberField",
        path: `${product.productClassName}.weeklyWorkingHours`,
        isHidden: ({ product }: { product: EmploymentContract }) =>
          !arrayUtils.getArraysIntersect(product.reasonsForDispute, ["overtime", "vacation"]) ||
          product.subjectOfDispute !== "money",
        numberType: "float",
      },
      {
        type: "ValidatorSelect",
        path: `${product.productClassName}.hasOvertimeSurcharge`,
        isHidden: ({ product }: { product: EmploymentContract }) =>
          !(product.reasonsForDispute.includes("overtime") && product.subjectOfDispute === "money"),
        options: () => trueFalseDefaultOptions(`${product.productClassName}.hasOvertimeSurcharge`),
      },
      {
        type: "ExpandableTextField",
        path: `${product.productClassName}.overtimeSurchargeDetails`,
        isHidden: ({ product }: { product: EmploymentContract }) =>
          !product.reasonsForDispute.includes("overtime") ||
          product.subjectOfDispute !== "money" ||
          product.hasOvertimeSurcharge !== true,
        validators: textFieldDefault,
        additionalProps: {
          multiline: true,
          fullWidth: true,
        },
      },
      //overtime
      {
        type: "ValidatorDateField",
        path: `${product.productClassName}.overtimeAccumulatedStartDate`,
        validators: dateFieldDefault,
        isHidden: ({ product }: { product: EmploymentContract }) => !product.reasonsForDispute.includes("overtime"),
      },
      {
        type: "ValidatorDateField",
        path: `${product.productClassName}.overtimeAccumulatedEndDate`,
        validators: dateFieldDefault,
        isHidden: ({ product }: { product: EmploymentContract }) => !product.reasonsForDispute.includes("overtime"),
      },
      {
        type: "ValidatorNumberField",
        path: `${product.productClassName}.hoursLeisureCompensation`,
        isHidden: ({ product }: { product: EmploymentContract }) =>
          !product.reasonsForDispute.includes("overtime") || product.subjectOfDispute === "money",
        numberType: "float",
      },
      {
        type: "ValidatorSelect",
        path: `${product.productClassName}.hasOvertimeRecord`,
        isHidden: ({ product }: { product: EmploymentContract }) => !product.reasonsForDispute.includes("overtime"),
        options: () => trueFalseDefaultOptions(`${product.productClassName}.hasOvertimeRecord`),
      },
      //vacation & subjectOfDispute money
      {
        type: "ValidatorNumberField",
        path: `${product.productClassName}.grossSalarySumLastThreeMonths`,
        isHidden: ({ product }: { product: EmploymentContract }) =>
          !product.reasonsForDispute.includes("vacation") || product.subjectOfDispute !== "money",
        fieldType: "money",
        numberType: "float",
      },
      {
        type: "ValidatorNumberField",
        path: `${product.productClassName}.grossSalaryClaimFromVacationDays`,
        isHidden: ({ product }: { product: EmploymentContract }) =>
          !(product.reasonsForDispute.includes("vacation") && product.subjectOfDispute === "money"),
        isDisabled: () => true,
        fieldType: "money",
        numberType: "float",
        helperText:
          "Feld wird automatisch nach Mandantenangaben berechnet, sollte aber noch einmal händisch überprüft werden",
      },
      //vacation
      {
        type: "ValidatorNumberField",
        path: `${product.productClassName}.vacationDaysLeft`,
        numberType: "float",
        isHidden: ({ product }: { product: EmploymentContract }) => !product.reasonsForDispute.includes("vacation"),
      },
      {
        type: "ValidatorSelect",
        path: `${product.productClassName}.vacationDenialReason`,
        isHidden: ({ product }: { product: EmploymentContract }) =>
          !product.reasonsForDispute.includes("vacation") || product.subjectOfDispute === "money",
        options: () => [
          { value: "operationalNecessity" },
          { value: "noVacationDays" },
          { value: "requestNotOnTime" },
          { value: "noReasonProvided" },
          { value: "other" },
        ],
      },
      {
        type: "ValidatorDateField",
        path: `${product.productClassName}.yearOfVacationClaim`,
        validators: dateFieldDefault,
        isHidden: ({ product }: { product: EmploymentContract }) => !product.reasonsForDispute.includes("vacation"),
        additionalProps: {
          views: ["year"],
          openTo: "year",
          InputLabelProps: {
            shrink: true,
          },
          format: "yyyy",
        },
      },
      // workCertificate
      {
        type: "ValidatorSelect",
        path: `${product.productClassName}.workCertificateRequest`,
        isHidden: ({ product }: { product: EmploymentContract }) => !product.reasonsForDispute.includes("vacation"),
        options: () => [{ value: "requestIssuance" }, { value: "requestCorrection" }],
      },
      {
        type: "ValidatorSelect",
        path: `${product.productClassName}.workCertificateIssuanceDenied`,
        isHidden: ({ product }: { product: EmploymentContract }) =>
          !product.reasonsForDispute.includes("workCertificate") ||
          product.workCertificateRequest !== "requestIssuance",
        options: () => trueFalseDefaultOptions(`${product.productClassName}.workCertificateIssuanceDenied`),
      },
      {
        type: "ValidatorSelect",
        path: `${product.productClassName}.workCertificateIssuanceDenialReason`,
        isHidden: ({ product }: { product: EmploymentContract }) =>
          !product.reasonsForDispute.includes("workCertificate") ||
          product.workCertificateRequest !== "requestIssuance",
        options: () => [
          { value: "noEntitlement" },
          { value: "unresolvedDispute" },
          { value: "insufficientCapacity" },
          { value: "noReasonProvided" },
          { value: "other" },
        ],
      },
      {
        type: "ValidatorDateField",
        path: `${product.productClassName}.workCertificateReceivedAtDate`,
        validators: dateFieldDefault,
        isHidden: ({ product }: { product: EmploymentContract }) =>
          !product.reasonsForDispute.includes("workCertificate") ||
          product.workCertificateRequest !== "requestIssuance",
      },
      {
        type: "ValidatorSelect",
        path: `${product.productClassName}.workCertificateCorrectionRequest`,
        isHidden: ({ product }: { product: EmploymentContract }) =>
          !product.reasonsForDispute.includes("workCertificate") ||
          product.workCertificateRequest !== "requestCorrection",
        additionalProps: {
          multiple: true,
        },
        options: () => [
          { value: "performanceEvaluation" },
          { value: "conductEvaluation" },
          { value: "incompleteJobDescription" },
          { value: "missingKeyAchievements" },
          { value: "factualErrors" },
          { value: "formalErrors" },
          { value: "negativePhrasingOrCodes" },
          { value: "inadequateClosingStatement" },
          { value: "other" },
        ],
      },
      // parentalLeave
      {
        type: "ValidatorDateField",
        path: `${product.productClassName}.parentalLeaveStartDate`,
        validators: dateFieldDefault,
        isHidden: ({ product }: { product: EmploymentContract }) =>
          !product.reasonsForDispute.includes("parentalLeave"),
      },
      {
        type: "ValidatorDateField",
        path: `${product.productClassName}.parentalLeaveEndDate`,
        validators: dateFieldDefault,
        isHidden: ({ product }: { product: EmploymentContract }) =>
          !product.reasonsForDispute.includes("parentalLeave"),
      },
      {
        type: "ValidatorSelect",
        path: `${product.productClassName}.hasTakenParentalLeaveBefore`,
        isHidden: ({ product }: { product: EmploymentContract }) =>
          !product.reasonsForDispute.includes("parentalLeave"),
        options: () => trueFalseDefaultOptions(`${product.productClassName}.hasTakenParentalLeaveBefore`),
      },
      {
        type: "ValidatorSelect",
        path: `${product.productClassName}.parentalLeaveSplit`,
        isHidden: ({ product }: { product: EmploymentContract }) =>
          !product.reasonsForDispute.includes("parentalLeave"),
        options: () => trueFalseDefaultOptions(`${product.productClassName}.parentalLeaveSplit`),
      },
      {
        type: "ValidatorSelect",
        path: `${product.productClassName}.parentalLeaveRequestOnTime`,
        isHidden: ({ product }: { product: EmploymentContract }) =>
          !product.reasonsForDispute.includes("parentalLeave"),
        options: () => trueFalseDefaultOptions(`${product.productClassName}.parentalLeaveRequestOnTime`),
      },
      {
        type: "ValidatorSelect",
        path: `${product.productClassName}.parentalLeaveRequestDenied`,
        isHidden: ({ product }: { product: EmploymentContract }) =>
          !product.reasonsForDispute.includes("parentalLeave"),
        options: () => trueFalseDefaultOptions(`${product.productClassName}.parentalLeaveRequestDenied`),
      },
      {
        type: "ValidatorSelect",
        path: `${product.productClassName}.parentalLeaveRequestDeniedReason`,
        isHidden: ({ product }: { product: EmploymentContract }) =>
          !product.reasonsForDispute.includes("parentalLeave") || product.parentalLeaveRequestDenied !== true,
        options: () => [
          { value: "requestNotOnTime" },
          { value: "operationalNecessity" },
          { value: "formalErrors" },
          { value: "entitlementDisputedByEmployer" },
          { value: "noReasonProvided" },
          { value: "other" },
        ],
      },
      // partTime
      {
        type: "ValidatorDateField",
        path: `${product.productClassName}.partTimeStartDate`,
        validators: dateFieldDefault,
        isHidden: ({ product }: { product: EmploymentContract }) => !product.reasonsForDispute.includes("partTime"),
      },
      {
        type: "ValidatorNumberField",
        path: `${product.productClassName}.partTimeDesiredWeeklyWorkingHours`,
        isHidden: ({ product }: { product: EmploymentContract }) => !product.reasonsForDispute.includes("partTime"),
        numberType: "float",
      },
      {
        type: "ValidatorNumberField",
        path: `${product.productClassName}.numberOfEmployees`,
        isHidden: ({ product }: { product: EmploymentContract }) => !product.reasonsForDispute.includes("partTime"),
        numberType: "int",
      },
      {
        type: "ValidatorSelect",
        path: `${product.productClassName}.partTimeDenialReason`,
        isHidden: ({ product }: { product: EmploymentContract }) => !product.reasonsForDispute.includes("partTime"),
        options: () => [
          { value: "operationalReasons" },
          { value: "disproportionateCosts" },
          { value: "requirementsNotMet" },
          { value: "formalErrors" },
          { value: "noReasonProvided" },
          { value: "other" },
        ],
      },
      // bullying
      {
        type: "ValidatorSelect",
        path: `${product.productClassName}.incidentType`,
        isHidden: ({ product }: { product: EmploymentContract }) => !product.reasonsForDispute.includes("bullying"),
        options: () => [{ value: "bullying" }, { value: "discrimination" }],
      },
      {
        type: "ValidatorDateField",
        path: `${product.productClassName}.incidentStartDate`,
        validators: dateFieldDefault,
        isHidden: ({ product }: { product: EmploymentContract }) => !product.reasonsForDispute.includes("bullying"),
      },
      {
        type: "ValidatorSelect",
        path: `${product.productClassName}.incidentObjectiveClient`,
        isHidden: ({ product }: { product: EmploymentContract }) => !product.reasonsForDispute.includes("bullying"),
        options: () => [{ value: "injunction" }, { value: "compensation" }, { value: "other" }],
      },

      // claimedPayment
      {
        type: "ValidatorSelect",
        path: `${product.productClassName}.claimedPaymentTypes`,
        isHidden: ({ product }: { product: EmploymentContract }) =>
          !product.reasonsForDispute.includes("claimedPayment"),
        additionalProps: {
          multiple: true,
        },
        options: () => [
          { value: "salary" },
          { value: "bonus" },
          { value: "training" },
          { value: "loan" },
          { value: "other" },
        ],
      },
      {
        type: "ExpandableTextField",
        path: `${product.productClassName}.claimedPaymentReason`,
        isHidden: ({ product }: { product: EmploymentContract }) =>
          !product.reasonsForDispute.includes("claimedPayment"),
        additionalProps: {
          multiline: true,
          fullWidth: true,
        },
      },
      // allegedDamage
      {
        type: "ValidatorSelect",
        path: `${product.productClassName}.allegedDamageTypes`,
        isHidden: ({ product }: { product: EmploymentContract }) =>
          !product.reasonsForDispute.includes("allegedDamage"),
        additionalProps: {
          multiple: true,
        },
        options: () => [
          { value: "damageOrLossOfProperty" },
          { value: "breachOfNonCompete" },
          { value: "breachOfConfidentiality" },
          { value: "poorPerformance" },
          { value: "embezzlementOrTheft" },
          { value: "other" },
        ],
      },
      {
        type: "ValidatorDateField",
        path: `${product.productClassName}.allegedDamageDate`,
        validators: dateFieldDefault,
        isHidden: ({ product }: { product: EmploymentContract }) =>
          !product.reasonsForDispute.includes("allegedDamage"),
      },
      // writtenWarning
      {
        type: "ValidatorDateField",
        path: `${product.productClassName}.writtenWarningReceivedDate`,
        validators: dateFieldDefault,
        isHidden: ({ product }: { product: EmploymentContract }) =>
          !product.reasonsForDispute.includes("writtenWarning"),
      },
      {
        type: "ValidatorSelect",
        path: `${product.productClassName}.writtenWarningIllegalReasons`,
        isHidden: ({ product }: { product: EmploymentContract }) =>
          !product.reasonsForDispute.includes("writtenWarning"),
        additionalProps: {
          multiple: true,
        },
        options: () => [
          { value: "factuallyIncorrect" },
          { value: "disproportionateWarning" },
          { value: "formalErrors" },
          { value: "other" },
        ],
      },
      {
        type: "ExpandableTextField",
        path: `${product.productClassName}.writtenWarningReason`,
        isHidden: ({ product }: { product: EmploymentContract }) =>
          !product.reasonsForDispute.includes("writtenWarning"),
        validators: textFieldDefault,
        additionalProps: {
          multiline: true,
          fullWidth: true,
        },
      },
      // termination
      {
        type: "ValidatorDateField",
        path: `${product.productClassName}.terminationAgreementOfferDate`,
        validators: dateFieldDefault,
        isHidden: ({ product }: { product: EmploymentContract }) => !product.reasonsForDispute.includes("termination"),
      },
      {
        type: "ValidatorDateField",
        path: `${product.productClassName}.terminationAgreementDeadlineDate`,
        validators: dateFieldDefault,
        isHidden: ({ product }: { product: EmploymentContract }) => !product.reasonsForDispute.includes("termination"),
      },
      {
        type: "ValidatorSelect",
        path: `${product.productClassName}.terminationAgreementIncludesSettlement`,
        isHidden: ({ product }: { product: EmploymentContract }) => !product.reasonsForDispute.includes("termination"),
        options: () => trueFalseDefaultOptions(`${product.productClassName}.terminationAgreementIncludesSettlement`),
      },
      {
        type: "ValidatorNumberField",
        path: `${product.productClassName}.offeredSettlementPayment`,
        isHidden: ({ product }: { product: EmploymentContract }) =>
          !product.reasonsForDispute.includes("termination") || product.terminationAgreementIncludesSettlement !== true,
        fieldType: "money",
        numberType: "float",
      },
      {
        type: "ValidatorSelect",
        path: `${product.productClassName}.terminationAgreementObjectivesClient`,
        isHidden: ({ product }: { product: EmploymentContract }) => !product.reasonsForDispute.includes("termination"),
        additionalProps: {
          multiple: true,
        },
        options: () => [
          { value: "maximizeSettlement" },
          { value: "avoidUnemploymentPenalty" },
          { value: "securePositiveWorkCertificate" },
          { value: "achieveImmediateRelease" },
          { value: "negotiateOtherTerms" },
          { value: "remainEmployed" },
          { value: "other" },
        ],
      },
      // itemsToReturn
      {
        type: "ValidatorSelect",
        path: `${product.productClassName}.itemsToReturn`,
        isHidden: ({ product }: { product: EmploymentContract }) =>
          !product.reasonsForDispute.includes("itemsToReturn"),
        additionalProps: {
          multiple: true,
        },
        options: () => [
          { value: "companyCar" },
          { value: "laptopOrTablet" },
          { value: "companyPhone" },
          { value: "keysOrAccessCards" },
          { value: "toolsOrEquipment" },
          { value: "companyDocuments" },
          { value: "other" },
        ],
      },
      {
        type: "ValidatorSelect",
        path: `${product.productClassName}.itemsToReturnRejectionReasons`,
        isHidden: ({ product }: { product: EmploymentContract }) =>
          !product.reasonsForDispute.includes("itemsToReturn"),
        additionalProps: {
          multiple: true,
        },
        options: () => [
          { value: "itemsBelongToMe" },
          { value: "itemsNeededForWork" },
          { value: "notContractuallyAgreed" },
          { value: "disproportionateClaim" },
          { value: "claimExpired" },
          { value: "other" },
        ],
      },
      {
        type: "ExpandableTextField",
        path: `${product.productClassName}.itemsToReturnReason`,
        isHidden: ({ product }: { product: EmploymentContract }) =>
          !product.reasonsForDispute.includes("itemsToReturn"),
        validators: textFieldDefault,
        additionalProps: {
          multiline: true,
          fullWidth: true,
        },
      },
    ],
  };

  const employmentContractDescription = {
    isHidden: ({ product }: { product: EmploymentContract }) => product.stage! < stagesCase.STAGE_DATA_COMPLETED,
    label: "Beschreibung der Situation",
    elements: [
      {
        type: "ValidatorTextField",
        path: `${product.productClassName}.personalTextMessage`,
        isDisabled: () => true,
        additionalProps: {
          multiline: true,
          fullWidth: true,
        },
      },
    ],
  };

  const opposingParty = {
    isHidden: ({ product }: { product: EmploymentContract }) =>
      product.advisoryHandling || product.stage < stagesCase.STAGE_DATA_COMPLETED,
    label: "Gegenseite",
    elements: [
      {
        type: "ValidatorSelect",
        path: `${product.productClassName}.opposingPartyType`,
        isDisabled: ({ product }: { product: EmploymentContract }) => product.stage >= stagesCase.STAGE_COMPLETED,
        options: () => [{ value: "opposingPartyLegalEntity" }, { value: "opposingPartyPerson" }],
        additionalProps: {
          fullWidth: true,
        },
      },
      {
        type: "ValidatorTextField",
        path: `${product.productClassName}.opposingPartyLegalEntity.organization.legalName`,
        isDisabled: ({ product }: { product: EmploymentContract }) => product.stage >= stagesCase.STAGE_COMPLETED,
        isHidden: ({ values }: { values: EmploymentContract }) =>
          values.employmentContract.opposingPartyType !== "opposingPartyLegalEntity",
        validators: textFieldDefault,
      },
      {
        type: "ValidatorSelect",
        path: `${product.productClassName}.opposingPartyLegalEntity.organization.legalForm`,
        isDisabled: ({ product }: { product: EmploymentContract }) => product.stage >= stagesCase.STAGE_COMPLETED,
        isHidden: ({ values }: { values: EmploymentContract }) =>
          values.employmentContract.opposingPartyType !== "opposingPartyLegalEntity",
        options: () => [
          { value: "__null__", label: "Keine Gesellschaftsform" },
          ..._.map(legalForms, (legalForm) => {
            return {
              value: legalForm.label,
              label: legalForm.label,
            };
          }),
        ],
      },
      {
        type: "ValidatorTextField",
        path: `${product.productClassName}.opposingPartyLegalEntity.organization.address.streetAddress`,
        isDisabled: ({ product }: { product: EmploymentContract }) => product.stage >= stagesCase.STAGE_COMPLETED,
        isHidden: ({ values }: { values: UseForm["values"] }) =>
          values.employmentContract.opposingPartyType !== "opposingPartyLegalEntity",
        validators: textFieldDefault,
      },
      {
        type: "ValidatorTextField",
        path: `${product.productClassName}.opposingPartyLegalEntity.organization.address.postalCode`,
        isDisabled: ({ product }: { product: EmploymentContract }) => product.stage >= stagesCase.STAGE_COMPLETED,
        isHidden: ({ values }: { values: UseForm["values"] }) =>
          values.employmentContract.opposingPartyType !== "opposingPartyLegalEntity",
        validators: [...textFieldDefault, postalCodeDefault],
      },
      {
        type: "ValidatorTextField",
        path: `${product.productClassName}.opposingPartyLegalEntity.organization.address.addressLocality`,
        isDisabled: ({ product }: { product: EmploymentContract }) => product.stage >= stagesCase.STAGE_COMPLETED,
        isHidden: ({ values }: { values: UseForm["values"] }) =>
          values.employmentContract.opposingPartyType !== "opposingPartyLegalEntity",
        validators: textFieldDefault,
      },
      {
        type: "ValidatorTextField",
        path: `${product.productClassName}.opposingPartyLegalEntity.organization.address.postOfficeBoxNumber`,
        isDisabled: ({ product }: { product: EmploymentContract }) => product.stage >= stagesCase.STAGE_COMPLETED,
        isHidden: ({ values }: { values: UseForm["values"] }) =>
          values.employmentContract.opposingPartyType !== "opposingPartyLegalEntity",
        validators: textFieldDefault,
      },
      {
        type: "ValidatorTextField",
        path: `${product.productClassName}.opposingPartyLegalEntity.organization.address.postOfficeBoxPostalCode`,
        isDisabled: ({ product }: { product: EmploymentContract }) => product.stage >= stagesCase.STAGE_COMPLETED,
        isHidden: ({ values }: { values: UseForm["values"] }) =>
          values.employmentContract.opposingPartyType !== "opposingPartyLegalEntity",
        validators: [...textFieldDefault, postalCodeDefault],
      },
      {
        type: "ValidatorTextField",
        path: `${product.productClassName}.opposingPartyLegalEntity.organization.address.postOfficeBoxAddressLocality`,
        isDisabled: ({ product }: { product: EmploymentContract }) => product.stage >= stagesCase.STAGE_COMPLETED,
        isHidden: ({ values }: { values: UseForm["values"] }) =>
          values.employmentContract.opposingPartyType !== "opposingPartyLegalEntity",
        validators: textFieldDefault,
      },
      {
        type: "ValidatorTextField",
        path: `${product.productClassName}.opposingPartyLegalEntity.organization.mainEmail.email`,
        isDisabled: ({ product }: { product: EmploymentContract }) => product.stage >= stagesCase.STAGE_COMPLETED,
        isHidden: ({ values }: { values: UseForm["values"] }) =>
          values.employmentContract.opposingPartyType !== "opposingPartyLegalEntity",
        validators: textFieldDefault,
      },
      {
        type: "ValidatorTextField",
        path: `${product.productClassName}.opposingPartyLegalEntity.organization.telephone`,
        isDisabled: ({ product }: { product: EmploymentContract }) => product.stage >= stagesCase.STAGE_COMPLETED,
        isHidden: ({ values }: { values: UseForm["values"] }) =>
          values.employmentContract.opposingPartyType !== "opposingPartyLegalEntity",
        validators: textFieldDefault,
      },
      {
        type: "ValidatorTextField",
        path: `${product.productClassName}.opposingPartyLegalEntity.organization.fax`,
        isDisabled: ({ product }: { product: EmploymentContract }) => product.stage >= stagesCase.STAGE_COMPLETED,
        isHidden: ({ values }: { values: UseForm["values"] }) =>
          values.employmentContract.opposingPartyType !== "opposingPartyLegalEntity",
        validators: faxNumberDefault,
        additionalProps: {
          InputProps: {
            startAdornment: <InputAdornment position="start">+49</InputAdornment>,
          },
        },
      },
      {
        type: "ValidatorTextField",
        path: `${product.productClassName}.opposingPartyReference`,
        helperText: translate(`${product.productClassName}.opposingPartyReference.helperText`),
        isDisabled: ({ product }: { product: EmploymentContract }) => product.stage < stagesCase.STAGE_DATA_COMPLETED,
        isHidden: ({ values }: { values: UseForm["values"] }) =>
          values.employmentContract.opposingPartyType !== "opposingPartyLegalEntity",
      },
      {
        type: "ContractRepresentatives",
        path: `${product.productClassName}.opposingPartyLegalEntity.organization.additionalRepresentatives`,
        isHidden: ({ values }: { values: UseForm["values"] }) =>
          values.opposingPartyType === "opposingPartyLegalEntity",
        additionalProps: {
          fullWidth: true,
          gridSpacing: 4,
          gridSize: 4,
          buttonFullWidth: false,
        },
      },
      ...personFields({
        path: `${product.productClassName}.opposingPartyPerson`,
        isHidden: ({ values }: { values: UseForm["values"] }) =>
          values.employmentContract.opposingPartyType !== "opposingPartyPerson",
        isDisabled: ({ product }: { product: EmploymentContract; values: UseForm["values"] }) =>
          product.stage >= stagesCase.STAGE_COMPLETED,
      }),
      {
        type: "ValidatorTextField",
        path: `${product.productClassName}.opposingPartyPerson.email`,
        validators: emailDefault,
        isDisabled: ({ product }: { product: EmploymentContract }) => product.stage >= stagesCase.STAGE_COMPLETED,
        isHidden: ({ values }: { values: UseForm["values"] }) =>
          values.employmentContract.opposingPartyType !== "opposingPartyPerson",
      },
      {
        type: "ValidatorTextField",
        path: `${product.productClassName}.opposingPartyPerson.telephone`,
        isDisabled: ({ product }: { product: EmploymentContract }) => product.stage >= stagesCase.STAGE_COMPLETED,
        isHidden: ({ values }: { values: UseForm["values"] }) =>
          values.employmentContract.opposingPartyType !== "opposingPartyPerson",
      },
      ...addressFields({
        path: `${product.productClassName}.opposingPartyPerson.residenceAddress`,
        isHidden: ({ values }: { values: UseForm["values"] }) =>
          values.employmentContract.opposingPartyType !== "opposingPartyPerson",
        isDisabled: ({ product }: { product: EmploymentContract }) => product.stage >= stagesCase.STAGE_COMPLETED,
      }),
    ],
  };

  return {
    product: {
      sections: [
        contractBaseCreated,
        employmentContractBase,
        employmentContractDetails,
        employmentContractDescription,
        getAdditionalClientsForm(product),
        getUnderageClientForm(product, "Minderjährige Mandantschaft"),
      ],
    },
    additional: {
      sections: [
        {
          label: "Allgemeines",
          elements: [
            {
              type: "ValidatorTextField",
              path: `${product.productClassName}.reference`,
              isDisabled: () => true,
            },
            {
              type: "ValidatorTextField",
              path: `${product.productClassName}.powerOfAttorney`,
              isHidden: ({ product }: { product: EmploymentContract }) =>
                product.stage < stagesCase.STAGE_DATA_COMPLETED || product.caseCategory === "advisory",
              isDisabled: () => true,
            },
            {
              type: "ValidatorDateField",
              path: `${product.productClassName}.timeBarredDate`,
              validators: dateFieldDefault,
              isHidden: ({ product }: { product: EmploymentContract }) =>
                product.stage < stagesCase.STAGE_DATA_COMPLETED || product.advisoryHandling,
              isDisabled: ({ product }: { product: EmploymentContract }) => product.stage >= stagesCase.STAGE_CHECKED,
            },
            {
              type: "ValidatorSelect",
              path: `${product.productClassName}.caseCategoryAssessment`,
              isHidden: ({ product }: { product: EmploymentContract }) =>
                product.stage < stagesCase.STAGE_DATA_COMPLETED || product.caseCategory === "advisory",
              isDisabled: ({ product }: { product: EmploymentContract }) =>
                product.stage > stagesCase.STAGE_WAITING_FOR_FEEDBACK,
              options: () => [{ value: "confirm" }, { value: "reject" }],
            },
          ],
        },
        extraJudicialProcedure(product, currentUser),
        overwriteJudicialProcedure(product),
        {
          label: product.courtAppointments.length === 1 ? "Gerichtstermin" : "Gerichtstermine",
          isHidden: ({ product }: { product: EmploymentContract }) => product.courtAppointments.length === 0,
          elements: getCourtAppointmentFields(product),
        },
        getNextInstanceSection(product),
      ],
    },
    processParticipants: {
      asAccordion: true,
      sections: [
        opposingParty,
        caseInsurance(product),
        debtCollectionAgencyFields(product),
        opponentLawyer(product),
        prosecution(product),
      ],
    },
    additionalProcessParticipants: {
      isHidden: () => product.advisoryHandling,
      sections: [
        addProcessParticipant(),
        processParticipantsPeople(product),
        processParticipantsOrganizations(product),
      ],
      withoutSubmit: true,
    },
    court: {
      isHidden: () => product.advisoryHandling,
      sections: [
        litigationCourt(product, [
          "Amtsgericht",
          "Arbeitsgericht",
          "Landgericht",
          "Mahngericht",
          "Oberlandesgericht",
          "Landesarbeitsgericht",
        ]),
      ],
    },
  };
};
